import { useEffect, useRef, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

// Styled replacement for window.confirm — ONE look for every confirmation in the app (the native
// browser dialog reads as foreign next to the glass design). Dependency-free (no Dialog primitive in
// this project): fixed blurred overlay + card with the brand-gradient top bar and a tone-tinted icon
// badge. Esc / backdrop click cancel, aria dialog semantics, optional busy state while the confirmed
// action runs.
//
// FOCUS — two rules, both learned the hard way (user report 2026-09-02, « Send to Build »):
//  • The first TEXT FIELD wins when the message carries one, Cancel otherwise. `message` is a
//    ReactNode and several callers put a textarea in it; focusing Cancel then asked the reviewer to
//    click into the field before typing, in a dialog whose whole point was to type.
//  • And it is done ONLY when `open` becomes true. It used to sit in an effect that also depended on
//    `onCancel` — which every caller passes as an inline arrow, so a NEW identity on each render. One
//    keystroke re-rendered the parent, the effect re-ran, and the focus jumped back to Cancel: the
//    field was literally impossible to type in. The Escape listener keeps its own effect, because that
//    one MUST follow the latest onCancel.
//
// The "Enter can't confirm by accident" guard survives: Enter inside a textarea inserts a line, and
// the buttons are plain onClick handlers with no form to submit.

export type ConfirmTone = 'brand' | 'amber' | 'orange' | 'sky' | 'rose';

const TONE_BADGE: Record<ConfirmTone, string> = {
  brand: 'bg-rose-50 text-brand-rose',
  amber: 'bg-amber-50 text-amber-600',
  orange: 'bg-orange-50 text-orange-600',
  sky: 'bg-sky-50 text-sky-600',
  rose: 'bg-rose-50 text-rose-600',
};

export function ConfirmDialog({
  open,
  tone = 'brand',
  icon,
  title,
  message,
  confirmLabel,
  cancelLabel,
  secondaryLabel,
  onSecondary,
  busy = false,
  confirmDisabled = false,
  confirmEmphasis = 'primary',
  onCancel,
  onConfirm,
}: {
  open: boolean;
  tone?: ConfirmTone;
  icon?: ReactNode;
  title: string;
  message: ReactNode;
  confirmLabel: ReactNode;
  cancelLabel: string;
  /** Optional THIRD way out, offered on the left, away from Cancel/Confirm. For the dialogs whose real
   *  answer is "neither" — the reviewer wanted a different action, not to go ahead or give up. Rendered
   *  only when both this and onSecondary are given. */
  secondaryLabel?: ReactNode;
  onSecondary?: () => void;
  busy?: boolean;
  /** Grise le bouton de confirmation sans donner l'apparence d'un traitement en cours (`busy` met un
   *  spinner). Pour les dialogues dont la confirmation exige une saisie — un motif, par exemple :
   *  sans ça, le clic ne fait rien et le relecteur croit l'écran cassé. */
  confirmDisabled?: boolean;
  /** 'none' : la confirmation perd son dégradé et prend le même poids que la troisième issue. Pour les dialogues
   *  où aucune des deux réponses n'est « par défaut » (N29.4, 30-09 : « Build a-t-il confirmé ? »). */
  confirmEmphasis?: 'primary' | 'none';
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Le focus d'ouverture — dépend de `open` SEUL. Voir la note en tête : le remettre dans l'effet du
  // clavier le relancerait à chaque frappe.
  useEffect(() => {
    if (!open) return;
    const field = cardRef.current?.querySelector<HTMLElement>(
      'textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="number"]:not([disabled]), input:not([type]):not([disabled])',
    );
    (field ?? cancelRef.current)?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-[2px] p-4 animate-in fade-in duration-150"
      onClick={busy ? undefined : onCancel}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/5 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
      >
        <div className="h-1 bg-brand-gradient" aria-hidden="true" />
        <div className="p-5">
          <div className="flex items-start gap-3.5">
            {icon && (
              <span className={cn('shrink-0 w-11 h-11 rounded-xl flex items-center justify-center', TONE_BADGE[tone])}>
                {icon}
              </span>
            )}
            <div className="min-w-0">
              <h3 id="confirm-dialog-title" className="text-base font-bold text-slate-800">{title}</h3>
              <div className="text-sm text-slate-600 mt-1.5 leading-relaxed">{message}</div>
            </div>
          </div>
          <div className={cn('flex gap-2 mt-6 flex-wrap', secondaryLabel && onSecondary ? 'justify-between' : 'justify-end')}>
            {secondaryLabel && onSecondary && (
              <Button variant="outline" onClick={onSecondary} disabled={busy} className="border-sky-300 text-sky-700 hover:bg-sky-50">
                {secondaryLabel}
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
            <Button ref={cancelRef} variant="outline" onClick={onCancel} disabled={busy}>
              {cancelLabel}
            </Button>
            <Button
              variant={confirmEmphasis === 'none' ? 'outline' : undefined}
              onClick={onConfirm}
              disabled={busy || confirmDisabled}
              className={confirmEmphasis === 'none'
                ? 'border-rose-300 text-rose-700 hover:bg-rose-50'
                : 'bg-brand-gradient text-white hover:opacity-90'}
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              {confirmLabel}
            </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
